import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSaveMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { Page, ProcessHeader } from '@/components';
import MortgageRecommendation from '@/components/MortgageRecommendation';
import ProcessButton from '@/components/ProcessButton';
import offerIcon from '@/assets/images/offerIcon.svg?react'; // eslint-disable-line import/no-unresolved

const Offer = () => {
  const [loading, setLoading] = useState(false);
  const proposal = useSelector((state) => state.proposal);
  const proposalOffer = useSelector((state) => state.proposalOffer);
  const morgageCycleDetails = useSelector((state) => state.mortgageCycle);

  const {
    sortedMixesByRisk = [],
  } = proposalOffer;

  const { extraDetails } = proposal;
  const {
    loanAmount,
    mortgagePaymentCapacity,
    selectedRisk: storedSelectedRisk,
    period: storedPeriod,
    isRecycling,
  } = extraDetails;

  const isCycleLoan = isRecycling === 'yes';
  const { id } = proposal;
  const navigate = useNavigate();
  useIdInStoreGuard();

  // Calculate the overall min and max periods across all risk groups
  const minPeriod = Math.min(...sortedMixesByRisk.map((group) => group.minPeriod));
  const maxPeriod = Math.max(...sortedMixesByRisk.map((group) => group.maxPeriod));
  const [selectedYear, setSelectedYear] = useState(storedPeriod || maxPeriod);
  const [selectedRisk, setSelectedRisk] = useState(storedSelectedRisk || sortedMixesByRisk[0]?.risk || '1');
  const [riskOptions, setRiskOptions] = useState([sortedMixesByRisk[0].risk]);
  const [apiCg] = useSaveMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    const risks = [...new Set(sortedMixesByRisk.map((mix) => Number(mix.risk)))].sort();
    setRiskOptions(risks);
  }, [sortedMixesByRisk]);

  const marks = [];
  // eslint-disable-next-line no-plusplus
  for (let i = minPeriod; i <= maxPeriod; i++) {
    marks.push({
      value: i,
      label: '',
    });
  }

  const getMix = (risk) => {
    const group = sortedMixesByRisk.find((d) => d.risk === risk);
    return group?.mixes.find((mix) => mix.period === selectedYear);
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loanAmount').path, { id }));
  };

  const handleContinue = async () => {
    setLoading(true);
    try {
      dispatch(updateExtraDetails({ selectedRisk, period: selectedYear }));

      const selectedMix = getMix(selectedRisk);
      const totalSaving = Math.round(Number(selectedMix.bankTotalReturn)
                - Number(selectedMix.totalReturn));

      const objectToSend = {
        id,
        risk: riskOptions,
        period: selectedYear,
        totalSaving,
      };

      await getMutationResult(objectToSend, apiCg);

      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'borrowersDetails').path, { id }));
    } catch {
      // empty
    } finally {
      setLoading(false);
    }
  };

  const headerSubTitle = isCycleLoan ? 'פרטי ההצעה למחזור והרחבת משכנתא' : '';

  return (
    <ProcessHeader
      headerText="פרטי ההצעה"
      headerColor="#E0EDFF"
      headerTextColor="#3348FF"
      showReturnButton
      onReturn={handleReturn}
      icon={offerIcon}
      subtitle={headerSubTitle}
    >

      <Page.Section>
        <MortgageRecommendation
          data={sortedMixesByRisk}
          loanAmount={loanAmount}
          mortgagePaymentCapacity={mortgagePaymentCapacity}
          morgageCycleDetails={morgageCycleDetails}
          proposal={proposal}
          period={selectedYear}
          minPeriod={minPeriod}
          maxPeriod={maxPeriod}
          onPeriodChange={setSelectedYear}
          selectedRisk={selectedRisk}
          onRiskChange={setSelectedRisk}
          getMix={getMix}
        />
      </Page.Section>

      <Page.Actions>
        <ProcessButton onClick={handleContinue} loading={loading} />
      </Page.Actions>
    </ProcessHeader>
  );
};

export default Offer;
